import { SagaIterator } from "redux-saga";
import {
  all,
  call,
  debounce,
  fork,
  put,
  select,
  take,
  takeLatest,
} from "redux-saga/effects";

import {
  cancelSubscriptionError,
  cancelSubscriptionSuccess,
  createSubscriptionError,
  createSubscriptionSuccess,
  editSubscriptionOptionsError,
  editSubscriptionOptionsSuccess,
  fetchCurrentSubscriptionSuccess,
  fetchDataFailure,
  fetchDataRequest,
  fetchSubscriptionList,
  fetchSubscriptionOptions,
  fetchSubscriptionOptionsError,
  fetchSubscriptionOptionsSuccess,
  fetchSubscriptionPreview,
  fetchSubscriptionPreviewError,
  recycleSubscriptionError,
  recycleSubscriptionSuccess,
  setNoCurrentSubscription,
  setOfferingDetails,
  setSubscriptionDetailsById,
  setSubscriptionList,
  setSubscriptionPreview,
  uncancelSubscriptionError,
  uncancelSubscriptionSuccess,
  updateSubscriptionError,
  updateSubscriptionSuccess,
} from "./actions";
import {
  CANCEL_SUBSCRIPTION,
  CLEAR_SUBSCRIPTIONS_STATE,
  CREATE_SUBSCRIPTION,
  EDIT_OPTIONS,
  FETCH_CURRENT_SUBSCRIPTION,
  FETCH_DATA_FAILURE,
  FETCH_DATA_REQUEST,
  FETCH_OPTIONS,
  FETCH_SUBSCRIPTION_PREVIEW_TRIGGER,
  FETCH_UPDATE_SUBSCRIPTION_PREVIEW_TRIGGER,
  PAUSE_SUBSCRIPTION,
  RECYCLE_SUBSCRIPTION,
  UNCANCEL_SUBSCRIPTION,
  UNPAUSE_SUBSCRIPTION,
  UPDATE_SUBSCRIPTION,
} from "./constants";
import {
  ICancelSubscription,
  ICreateSubscription,
  IEditSubscriptionOptions,
  IFetchDataRequestAction,
  IFetchSubscriptionOptions,
  IFetchSubscriptionPreviewTrigger,
  IFetchUpdateSubscriptionPreviewTrigger,
  IPauseSubscription,
  IRecycleSubscription,
  IUncancelSubscription,
  IUpdateSubscription,
} from "./types";
import offeringAPIService from "../../services/api/offering";
import subscriptionAPIService from "../../services/api/subscription";
import {
  ISubscription,
  ISubscriptionDetails,
} from "../../services/api/subscription/types";
import {
  isSubscriptionOver,
  selectCurrentCustomerSubscription,
  selectCurrentSubscriptionDetail,
  selectSubscriptionOptions,
} from "./selectors";
import { EXTERNAL_SP_LIST } from "../../constants/spCode";
import {
  SET_CURRENT_PRODUCT_LINE,
  selectCurrentProductLine,
} from "../metadata";
import { selectPrincipalData } from "../user";
import { fetchUsage } from "../usage";
import {
  fetchClearFeatureDetailsAction,
  fetchDetailsAction,
  fetchFeatureDetailsAction,
} from "../skuMapping";
import { fetchLicensesRequest } from "../manageUsers";
import abilityService from "../../services/ability";
import { ACTION, SUBJECT } from "../../constants/permissions";
import { getSubscriptionStatus } from "../../utils/common";
import { SUBSCRIPTION_STATUS } from "../../utils/common/contants";

export function* getCustomersSubscriptionsData({ payload }: any): SagaIterator {
  try {
    const customerSubscriptions: ISubscription[] = yield call(
      [subscriptionAPIService, subscriptionAPIService.getAll],
      payload.customerId,
      {
        embed: "(sku)",
      },
    );

    yield put(setSubscriptionList(customerSubscriptions));
    yield call(loadSubscriptionDetails, customerSubscriptions);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(
      `Failed to load the subscriptions of customer ${payload.customerId}`,
      e,
    );
    yield put(fetchDataFailure(e));
  }
}

interface ISubscriptionDetailResult {
  id: string;
  detail: ISubscriptionDetails | null;
}

// A secondary's failure only leaves it out of the map; only the current one's failure is
// page-level.
function* fetchDetailForSubscription(
  sub: ISubscription,
): SagaIterator<ISubscriptionDetailResult> {
  try {
    const detail = yield call(
      [subscriptionAPIService, subscriptionAPIService.getDetail],
      sub.customer_id,
      sub.id,
    );

    return { id: sub.id, detail };
  } catch {
    return { id: sub.id, detail: null };
  }
}

function* fetchAndPublishCurrentSubscription(
  currentSubscription: ISubscription,
): SagaIterator<ISubscriptionDetailResult> {
  let detail: ISubscriptionDetails | null = null;

  try {
    const fetched: ISubscriptionDetails | null | undefined = yield call(
      [subscriptionAPIService, subscriptionAPIService.getDetail],
      currentSubscription.customer_id,
      currentSubscription.id,
    );

    if (!fetched) {
      throw new Error(
        `Empty detail for subscription ${currentSubscription.id}`,
      );
    }

    detail = fetched;

    yield put(fetchCurrentSubscriptionSuccess(detail));
    yield put(fetchDetailsAction({ skuId: currentSubscription.sku_id }));

    if (abilityService.getAbility().can(ACTION.READ, SUBJECT.TECHNICAL)) {
      yield put(fetchLicensesRequest(currentSubscription.id));
    }

    const { upcoming } = detail;
    if (upcoming && currentSubscription.sku_id !== upcoming.sku_id) {
      yield put(fetchFeatureDetailsAction({ skuId: upcoming.sku_id }));
    }
  } catch (e) {
    yield put(fetchDataFailure(e));
  }

  return { id: currentSubscription.id, detail };
}

// One get-detail per subscription that is not over (the portal never lists over ones), fetched in
// parallel. The current one is published as soon as it arrives: PrivateRoute waits on it, not on
// the map.
export function* loadSubscriptionDetails(
  subscriptions: ISubscription[],
): SagaIterator {
  const currentSubscription: ISubscription | undefined = yield select(
    selectCurrentCustomerSubscription,
  );

  if (!currentSubscription) {
    yield put(setNoCurrentSubscription());
  }

  const otherRunning = subscriptions.filter((sub) => {
    return sub.id !== currentSubscription?.id && !isSubscriptionOver(sub);
  });

  const results: ISubscriptionDetailResult[] = yield all([
    ...(currentSubscription
      ? [call(fetchAndPublishCurrentSubscription, currentSubscription)]
      : []),
    ...otherRunning.map((sub) => {
      return call(fetchDetailForSubscription, sub);
    }),
  ]);

  const detailsById: Record<string, ISubscriptionDetails> = {};
  results.forEach((result) => {
    if (result.detail) {
      detailsById[result.id] = result.detail;
    }
  });

  yield put(setSubscriptionDetailsById(detailsById));
}

export function* getOfferingsData(): SagaIterator {
  const subscription = yield select(selectCurrentCustomerSubscription);
  const subscriptionDetails = yield select(selectCurrentSubscriptionDetail);
  const currentProductLine = yield select(selectCurrentProductLine);

  const status =
    subscriptionDetails && getSubscriptionStatus(subscriptionDetails);
  const isShowDefault =
    !subscription ||
    [SUBSCRIPTION_STATUS.notActive, SUBSCRIPTION_STATUS.expired].includes(
      status,
    );

  try {
    const defaultOfferingCode = currentProductLine?.default_offering_code;

    if (
      (isShowDefault ||
        EXTERNAL_SP_LIST.includes(subscriptionDetails?.sp.sp_code)) &&
      defaultOfferingCode
    ) {
      yield call(fetchDefaultOfferings, defaultOfferingCode);
      return;
    }

    if (!subscription?.offering_code) {
      return;
    }

    const offeringDetailsData = yield call(
      [offeringAPIService, offeringAPIService.getDetails],
      subscription?.offering_code,
    );
    yield put(setOfferingDetails(offeringDetailsData));
  } catch (e) {
    yield put(fetchDataFailure(e));
  }
}

export function* fetchDefaultOfferings(offeringCode: string): SagaIterator {
  try {
    const offeringDetailsData = yield call(
      [offeringAPIService, offeringAPIService.getDetails],
      offeringCode,
    );
    yield put(setOfferingDetails(offeringDetailsData));
  } catch (e) {
    yield put(fetchDataFailure(e));
  }
}

export function* getData({ payload }: IFetchDataRequestAction): SagaIterator {
  yield call(
    getCustomersSubscriptionsData,
    fetchSubscriptionList({ customerId: payload.customerId }),
  );
  yield call(getOfferingsData);
}

function* getSubscriptionPreview({
  payload,
  onSuccess,
  onError,
}: IFetchSubscriptionPreviewTrigger): SagaIterator {
  yield put(fetchSubscriptionPreview());
  const principal = yield select(selectPrincipalData);

  try {
    const { skuId, currency, promotionToken, offeringCode } = payload;
    const subscriptionPreview = yield call(
      [subscriptionAPIService, subscriptionAPIService.previewSubscription],
      principal.customer_id,
      {
        sku_id: skuId,
        currency,
        promotion_token: promotionToken,
        offering_code: offeringCode,
      },
    );
    yield put(setSubscriptionPreview(subscriptionPreview));
    yield call(onSuccess, subscriptionPreview);
  } catch (error) {
    yield put(fetchSubscriptionPreviewError(error));
    yield call(onError, error);
  }
}

function* updateSubscriptionPreview({
  payload,
  onSuccess,
  onError,
}: IFetchUpdateSubscriptionPreviewTrigger): SagaIterator {
  yield put(fetchSubscriptionPreview());
  const currentSubscriptionDetail = yield select(
    selectCurrentSubscriptionDetail,
  );
  const principal = yield select(selectPrincipalData);

  try {
    const { skuId, addons, promotionToken } = payload;

    const subscriptionPreview = yield call(
      [
        subscriptionAPIService,
        subscriptionAPIService.previewUpdateSubscription,
      ],
      principal.customer_id,
      currentSubscriptionDetail.subscription_id,
      { addons, sku_id: skuId, promotion_token: promotionToken },
    );
    yield put(setSubscriptionPreview(subscriptionPreview));
    yield call(onSuccess);
  } catch (error) {
    yield put(fetchSubscriptionPreviewError(error));
    yield call(onError, error);
  }
}

function* updateSubscription({ payload }: IUpdateSubscription): SagaIterator {
  const {
    subscriptionId,
    promotionToken,
    skuId,
    addons,
    onSuccess,
    onError,
  } = payload;
  const principal = yield select(selectPrincipalData);

  try {
    const { timeframe } = yield call(
      [subscriptionAPIService, subscriptionAPIService.updateSubscription],
      principal.customer_id,
      subscriptionId,
      { addons, promotion_token: promotionToken, sku_id: skuId },
    );
    yield put(updateSubscriptionSuccess());
    yield put(fetchClearFeatureDetailsAction());
    yield put(fetchDataRequest({ customerId: principal.customer_id }));
    onSuccess && onSuccess(timeframe);
  } catch (e) {
    yield put(updateSubscriptionError());
    onError && onError(e);
  }
}

function* createSubscription({ payload }: ICreateSubscription): SagaIterator {
  const principal = yield select(selectPrincipalData);
  const currentProductLine = yield select(selectCurrentProductLine);
  const {
    currency,
    skuId,
    promotionToken,
    captchaToken,
    onSuccess,
    onError,
    billingInfo,
  } = payload;

  try {
    yield call(
      [subscriptionAPIService, subscriptionAPIService.create],
      {
        currency,
        sku_id: skuId,
        offering_code: currentProductLine.default_offering_code,
        billing_token: billingInfo,
        promotion_token: promotionToken,
        captcha_token: captchaToken,
      },
      principal.customer_id,
    );
    yield put(createSubscriptionSuccess());
    yield put(fetchDataRequest({ customerId: principal.customer_id }));
    onSuccess && onSuccess();
  } catch (error) {
    yield put(createSubscriptionError());
    onError && onError(error);
  }
}

function* cancelSubscription({ payload }: ICancelSubscription): SagaIterator {
  const { cancelReason, subscriptionId, onSuccess, onError } = payload;
  const principal = yield select(selectPrincipalData);
  const currentSubscriptionDetail = yield select(
    selectCurrentSubscriptionDetail,
  );

  try {
    yield call([subscriptionAPIService, subscriptionAPIService.cancel], {
      customerId: principal.customer_id,
      subscriptionId:
        subscriptionId ?? currentSubscriptionDetail.subscription_id,
      cancelReason: cancelReason,
    });

    yield put(cancelSubscriptionSuccess());
    yield put(fetchDataRequest({ customerId: principal.customer_id }));
    onSuccess && onSuccess();
  } catch (e) {
    yield put(cancelSubscriptionError());
    onError && onError();
  }
}

function* uncancelSubscription({
  payload,
}: IUncancelSubscription): SagaIterator {
  const { onSuccess, onError } = payload;
  const principal = yield select(selectPrincipalData);
  const currentSubscriptionDetail = yield select(
    selectCurrentSubscriptionDetail,
  );
  try {
    yield call([subscriptionAPIService, subscriptionAPIService.uncancel], {
      customerId: principal.customer_id,
      subscriptionId: currentSubscriptionDetail.subscription_id,
    });

    yield put(uncancelSubscriptionSuccess());
    yield put(fetchDataRequest({ customerId: principal.customer_id }));
    onSuccess && onSuccess();
  } catch (e) {
    yield put(uncancelSubscriptionError());
    onError && onError();
  }
}

export function* debouncePreview(): SagaIterator {
  yield debounce(
    400,
    (action: any) => {
      return action.type === FETCH_SUBSCRIPTION_PREVIEW_TRIGGER;
    },
    getSubscriptionPreview,
  );
}

function* pauseSubscription({ payload }: IPauseSubscription): SagaIterator {
  const { onSuccess, onError } = payload;
  const principal = yield select(selectPrincipalData);
  const currentSubscriptionDetail = yield select(
    selectCurrentSubscriptionDetail,
  );
  try {
    yield call([subscriptionAPIService, subscriptionAPIService.pause], {
      customerId: principal.customer_id,
      subscriptionId: currentSubscriptionDetail.subscription_id,
    });

    yield put(fetchDataRequest({ customerId: principal.customer_id }));
    onSuccess && onSuccess();
  } catch (e) {
    onError && onError();
  }
}

function* unpauseSubscription({ payload }: IPauseSubscription): SagaIterator {
  const { onSuccess, onError } = payload;
  const principal = yield select(selectPrincipalData);
  const currentSubscriptionDetail = yield select(
    selectCurrentSubscriptionDetail,
  );
  try {
    yield call([subscriptionAPIService, subscriptionAPIService.unpause], {
      customerId: principal.customer_id,
      subscriptionId: currentSubscriptionDetail.subscription_id,
    });

    yield put(fetchDataRequest({ customerId: principal.customer_id }));
    onSuccess && onSuccess();
  } catch (e) {
    onError && onError();
  }
}

export function* debounceUpdatePreview(): SagaIterator {
  yield debounce(
    400,
    (action: any) => {
      return action.type === FETCH_UPDATE_SUBSCRIPTION_PREVIEW_TRIGGER;
    },
    updateSubscriptionPreview,
  );
}

function* recycleSubscription({ payload }: IRecycleSubscription): SagaIterator {
  const principal = yield select(selectPrincipalData);
  const currentCustomerSubscription = yield select(
    selectCurrentCustomerSubscription,
  );
  const { subscriptionId, onError, onSuccess } = payload;

  try {
    yield call(
      [subscriptionAPIService, subscriptionAPIService.recycle],
      principal.customer_id,
      subscriptionId,
    );
    onSuccess && onSuccess();

    yield put(fetchDataRequest({ customerId: principal.customer_id }));
    yield put(recycleSubscriptionSuccess());
    yield put(
      fetchUsage({
        subscriptionId: currentCustomerSubscription.id,
      }),
    );
  } catch (e) {
    yield put(recycleSubscriptionError());
    yield put(fetchDataFailure(e));
    onError && onError();
  }
}

function* fetchOptions({ payload }: IFetchSubscriptionOptions): SagaIterator {
  const principal = yield select(selectPrincipalData);

  try {
    const options = yield call(
      [subscriptionAPIService, subscriptionAPIService.options],
      principal.customer_id,
      payload.subscriptionId,
    );
    yield put(fetchSubscriptionOptionsSuccess(options));
  } catch (e) {
    yield put(fetchSubscriptionOptionsError(e));
  }
}

function* editOptions({ payload }: IEditSubscriptionOptions): SagaIterator {
  const principal = yield select(selectPrincipalData);
  const options = yield select(selectSubscriptionOptions);
  const { subscriptionId, autoRecycle, onError, onSuccess } = payload;

  try {
    yield call(
      [subscriptionAPIService, subscriptionAPIService.editOptions],
      principal.customer_id,
      subscriptionId,
      autoRecycle,
      options?.eTag,
    );
    yield put(editSubscriptionOptionsSuccess());
    yield put(fetchSubscriptionOptions({ subscriptionId: subscriptionId }));
    onSuccess && onSuccess();
  } catch (error) {
    yield put(editSubscriptionOptionsError());
    onError && onError(error);
  }
}

function* watchGetData(): SagaIterator {
  yield takeLatest([FETCH_DATA_REQUEST], getData);
}

// Pages re-set the same line on every product-lines fetch; reload only when customer or line
// changed, or after the state was cleared or any subscriptions failure (offerings/recycle emit it).
export function* watchUpdateProductLineData(): SagaIterator {
  let loadedKey: string | undefined;
  while (true) {
    const action = yield take([
      SET_CURRENT_PRODUCT_LINE,
      CLEAR_SUBSCRIPTIONS_STATE,
      FETCH_DATA_FAILURE,
    ]);
    if (
      action.type === CLEAR_SUBSCRIPTIONS_STATE ||
      action.type === FETCH_DATA_FAILURE
    ) {
      loadedKey = undefined;
      continue;
    }

    const principal = yield select(selectPrincipalData);
    if (!principal) {
      continue;
    }

    const key = `${principal.customer_id}:${action.payload.code}`;
    if (key !== loadedKey) {
      loadedKey = key;
      yield put(fetchDataRequest({ customerId: principal.customer_id }));
    }
  }
}

function* watchFetchSubscriptionDetail(): SagaIterator {
  yield takeLatest(FETCH_CURRENT_SUBSCRIPTION, getCustomersSubscriptionsData);
}

function* watchRecycle(): SagaIterator {
  yield takeLatest(RECYCLE_SUBSCRIPTION, recycleSubscription);
}

function* watchFetchSubscriptionOptions(): SagaIterator {
  yield takeLatest(FETCH_OPTIONS, fetchOptions);
}

function* watchEditSubscriptionOptions(): SagaIterator {
  yield takeLatest(EDIT_OPTIONS, editOptions);
}

function* watchUpdateSubscription(): SagaIterator {
  yield takeLatest(UPDATE_SUBSCRIPTION, updateSubscription);
}

function* watchCreateSubscription(): SagaIterator {
  yield takeLatest(CREATE_SUBSCRIPTION, createSubscription);
}

function* watchCancelSubscription(): SagaIterator {
  yield takeLatest(CANCEL_SUBSCRIPTION, cancelSubscription);
}

function* watchUncancelSubscription(): SagaIterator {
  yield takeLatest(UNCANCEL_SUBSCRIPTION, uncancelSubscription);
}

function* watchPauseSubscription(): SagaIterator {
  yield takeLatest(PAUSE_SUBSCRIPTION, pauseSubscription);
}

function* watchUnpauseSubscription(): SagaIterator {
  yield takeLatest(UNPAUSE_SUBSCRIPTION, unpauseSubscription);
}

export function* subscriptionsSaga(): SagaIterator {
  yield all([
    fork(watchGetData),
    fork(watchFetchSubscriptionDetail),
    fork(watchUpdateProductLineData),
    fork(watchRecycle),
    fork(watchFetchSubscriptionOptions),
    fork(watchEditSubscriptionOptions),
    fork(debouncePreview),
    fork(debounceUpdatePreview),
    fork(watchUpdateSubscription),
    fork(watchCreateSubscription),
    fork(watchCancelSubscription),
    fork(watchUncancelSubscription),
    fork(watchPauseSubscription),
    fork(watchUnpauseSubscription),
  ]);
}
